import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { usePilotEnrollment } from "@/hooks/usePilotEnrollment";

const ROUTES: { label: string; to: string; group: string }[] = [
  { label: "Dashboard", to: "/", group: "Navigate" },
  { label: "Age-Friendly Index", to: "/age-friendly-index", group: "Navigate" },
  { label: "Assessments", to: "/assessments", group: "Navigate" },
  { label: "AI Insights", to: "/ai-insights", group: "Navigate" },
  { label: "Care and Wellbeing Marketplace", to: "/marketplace", group: "Ecosystem" },
  { label: "Experts & Services", to: "/expert-advisory", group: "Ecosystem" },
  { label: "Testbed", to: "/testbed-hub", group: "Ecosystem" },
  { label: "Services", to: "/services", group: "Ecosystem" },
  { label: "Organisations", to: "/organisations", group: "Manage" },
  { label: "Users", to: "/users", group: "Manage" },
  { label: "Service Requests", to: "/admin/service-requests", group: "Manage" },
  { label: "Marketplace Leads", to: "/admin/marketplace-leads", group: "Manage" },
  { label: "Vendor Management", to: "/admin/vendors", group: "Manage" },
  { label: "System Configuration", to: "/framework", group: "Manage" },
  { label: "Settings", to: "/settings", group: "Account" },
];

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { isPilotOnlyAssessor } = usePilotEnrollment();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const visibleRoutes = isPilotOnlyAssessor
    ? ROUTES.filter((route) => route.to !== "/ai-insights")
    : ROUTES;
  const grouped = visibleRoutes.reduce<Record<string, typeof ROUTES>>((acc, r) => {
    (acc[r.group] ||= []).push(r);
    return acc;
  }, {});

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        className="h-9 px-3 gap-2 text-sm font-normal text-muted-foreground rounded-full bg-muted/40 border-border/70 hover:bg-muted/70 hidden md:inline-flex min-w-[220px] justify-between"
      >
        <span className="inline-flex items-center gap-2">
          <Search className="h-4 w-4" />
          Search…
        </span>
        <kbd className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-background border border-border/70">
          ⌘K
        </kbd>
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen(true)}
        className="md:hidden h-9 w-9 rounded-full"
        aria-label="Search"
      >
        <Search className="h-4 w-4" />
      </Button>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Search pages, modules, settings…" />
        <CommandList>
          <CommandEmpty>No results.</CommandEmpty>
          {Object.entries(grouped).map(([group, items]) => (
            <CommandGroup key={group} heading={group}>
              {items.map((r) => (
                <CommandItem
                  key={r.to}
                  value={`${r.label} ${r.group}`}
                  onSelect={() => {
                    setOpen(false);
                    navigate(r.to);
                  }}
                >
                  {r.label}
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
}
